<template>
  <div style="width: 100%;">
    <v-row align="center" no-gutters>
      <div class="title text--secondary my-2 px-4 text-uppercase">
        {{ $t("mentorship.actions.title") }}
      </div>
      <v-spacer></v-spacer>
      <v-btn
        small
        class="primary accent-4 white--text mr-2"
        @click="
          print(
            'printMentorshipActionsFromSection',
            `${mentorshipAction.tag.name}_${mentorshipAction.versions.map(
              (v) => v.revision
            )}`
          )
        "
      >
        <v-icon small left>print</v-icon>
        {{ $t("profile.history.print") }}
      </v-btn>
    </v-row>
    <div v-if="$apollo.loading"></div>
    <section
      v-else-if="mentorshipAction"
      id="printMentorshipActionsFromSection"
    >
      <v-card
        class="mb-4"
        v-for="(version, index) in mentorshipAction.versions"
        :key="index"
      >
        <v-data-table
          :headers="headers"
          :items="getItems(version.content)"
          hide-default-footer
          show-expand
          :expanded.sync="expanded"
          disable-pagination
        >
          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length" style="padding-top: 16px;">
              <div v-html="item.content"></div>
            </td>
          </template>

          <template v-slot:top="{ items }">
            <v-card-subtitle>
              <div>
                {{ $t("mentorship.actions.column.tag") }}:
                {{
                  mentorshipAction.tag.description || mentorshipAction.tag.name
                }}
              </div>
              <div>
                {{ $t("mentorship.actions.column.code") }}:
                {{ mentorshipAction.code }}
              </div>
              <div>
                {{ $t("mentorship.actions.column.revision") }}
                {{ version.revision }}({{ version.created | date }})
              </div>
              <div>
                {{ $t("instructions.edit.language") }}
                <span class="text-uppercase">
                  {{ items[0].language }}
                </span>
              </div>
              <div>
                {{ $t("mentorship.actions.column.name") }}
                {{ getName(version, items[0].language) }}
              </div>
              <div v-if="version.mentorship">
                {{ $t("profile.mentorship.column.mentee") }}
                {{ version.mentorship.employee | nameId }}
              </div>
            </v-card-subtitle>
          </template>

          <template v-slot:[`item.title`]="{ item }">
            <div class="font-weight-bold">
              {{ item.title }}
            </div>
          </template>

          <template v-slot:[`item.mentor`]="{ item }">
            <span v-if="item.mentor"> {{ item.mentor | nameId }}</span>
          </template>

          <template v-slot:[`item.approveSignature`]="{ item }">
            <span v-if="item.approveSignature">
              {{ item.approveSignature.dateTime | date }}</span
            >
          </template>

          <template v-slot:[`item.confirmSignature`]="{ item }">
            <span v-if="item.confirmSignature">
              {{ item.confirmSignature.dateTime | date }}</span
            >
          </template>
        </v-data-table>
      </v-card>
    </section>

    <div v-else>no data found revision {{ revision }}</div>
  </div>
</template>

<script>
import MentorshipActionByTagIdQuery from "@/graphql/queries/MentorshipActionByTagId.gql";
import mentorshipActionsAssignedQuery from "@/graphql/queries/MentorshipActionsAssigned.gql";

import { print } from "@/utils/print";

import { getSignatureDerivedData } from "@/utils/mentorshipActions";

export default {
  props: {
    isAssigned: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      expanded: [],
      revStatuses: ["confirm", "approve", "assign"],
    };
  },
  computed: {
    revision() {
      return this.$route.query.revision || "";
    },
    language() {
      return this.$route.query.language || this.$i18n.locale;
    },
    headers() {
      return [
        {
          text: "",
          value: "index",
          class: "text-uppercase text-truncate",
          align: "left",
        },
        {
          text: this.$t(
            "profile.mentorship.actions.column.descriptionOfKnowledge"
          ),
          value: "title",
          class: "text-uppercase text-truncate",
          sortable: false,
        },
        {
          text: this.$t("profile.mentorship.actions.column.mentor"),
          value: "mentor",
          class: "text-uppercase text-truncate",
          sortable: false,
        },
        {
          text: this.$t("profile.mentorship.actions.column.mentorApproval"),
          value: "approveSignature",
          class: "text-uppercase text-truncate",
          sortable: false,
        },
        {
          text: this.$t("profile.mentorship.actions.column.menteeConfirmation"),
          value: "confirmSignature",
          class: "text-uppercase text-truncate",
          sortable: false,
        },
      ];
    },
  },
  methods: {
    getItems(content) {
      const items = content.map((c, index) => {
        const signatureDerivedData = getSignatureDerivedData(c.signatures);

        return {
          ...c,
          ...c.contentLocale,
          index: index + 1,
          ...signatureDerivedData,
        };
      });

      if (items.some((item) => item.language == this.language)) {
        return items.filter((item) => item.language == this.language);
      }

      if (items.length == 0) {
        return items;
      }

      const defaultLanguage = items[0].language;
      return items.filter((item) => item.language == defaultLanguage);
    },
    print(id, title) {
      print(id, title);
    },
    getName(version, language) {
      const item = version.names.find((name) => name.language == language);
      return item ? item.name : "";
    },
  },
  apollo: {
    mentorshipAction: {
      query() {
        return this.isAssigned
          ? mentorshipActionsAssignedQuery
          : MentorshipActionByTagIdQuery;
      },
      variables() {
        return this.isAssigned
          ? { mentorshipId: this.$route.params.id }
          : {
              tagId: this.$route.params.id,
            };
      },
      update(res) {
        if (this.revision) {
          res.mentorshipAction.versions = res.mentorshipAction.versions.filter(
            (v) => v.revision == this.revision
          );
        }

        this.$nextTick(() => {
          const itemsToExpand = [];
          res.mentorshipAction.versions.forEach((version) => {
            itemsToExpand.push(...this.getItems(version.content));
          });
          this.expanded = itemsToExpand;
        });
        return res.mentorshipAction;
      },
      skip() {
        return !this.$route.params.id;
      },
    },
  },
};
</script>
